import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import cn from 'classnames';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useMeasure } from 'react-use';

import type { CexOrder } from 'entities/cex-order';
import { addBignumbers, bn, divideBignumbers, humanizeBn, subtractBignumbers } from 'tools';
import { ICexPair } from 'types/pairs';
import { ETradeDirection } from 'types/trade-base';
import { ECexName } from 'web3';

import { LastPriceIndicator } from './LastPriceIndicator';
import { OrderbookHoverTooltip } from './OrderbookHoverTooltip';
import { OrderbookToolbar } from './OrderbookToolbar';
import { VirtualizedOrderbookList } from './VirtualizedOrderbookList';

import { roundPriceToTick } from '../lib/aggregateOrders';
import { useOrderbookData, useOrderbookSettings } from '../model/hooks';
import type { OrderbookSide, ProcessedOrderbookEntry } from '../model/types';

interface CexOrderbookProps {
  pair: ICexPair;
  className?: string;
  externalControl?: {
    tickSize?: number | null;
  };
  onSelectPriceAmount?: (price: string, amount: string) => void;
  orders?: CexOrder[];
}

const ORDER_CALC_DECIMALS = 18;

function sumOrderAmounts(orders: CexOrder[]): {
  baseAmountBn: ReturnType<typeof bn>;
  quoteAmountBn: ReturnType<typeof bn>;
} {
  let baseAmountBn = bn(0, ORDER_CALC_DECIMALS);
  let quoteAmountBn = bn(0, ORDER_CALC_DECIMALS);

  for (const order of orders) {
    const baseAmountBnValue = bn(order.base_amount, ORDER_CALC_DECIMALS);
    const factBaseAmountBn = bn(order.fact_base_amount, ORDER_CALC_DECIMALS);

    if (baseAmountBnValue.gt(factBaseAmountBn)) {
      const diffBase = subtractBignumbers(
        [baseAmountBnValue, ORDER_CALC_DECIMALS],
        [factBaseAmountBn, ORDER_CALC_DECIMALS],
      );
      baseAmountBn = addBignumbers(
        [baseAmountBn, ORDER_CALC_DECIMALS],
        [diffBase, ORDER_CALC_DECIMALS],
      );
    }

    const amountUsdBn = bn(order.amount_usd, ORDER_CALC_DECIMALS);
    const factQuoteAmountBn = bn(order.fact_quote_amount, ORDER_CALC_DECIMALS);

    if (amountUsdBn.isZero() && factQuoteAmountBn.isZero()) {
      const priceBn = bn(order.price, ORDER_CALC_DECIMALS);
      if (!baseAmountBnValue.isZero() && !priceBn.isZero()) {
        const fallbackQuote = divideBignumbers(
          [baseAmountBnValue, ORDER_CALC_DECIMALS],
          [priceBn, ORDER_CALC_DECIMALS],
        );
        quoteAmountBn = addBignumbers(
          [quoteAmountBn, ORDER_CALC_DECIMALS],
          [fallbackQuote, ORDER_CALC_DECIMALS],
        );
      }
      continue;
    }

    if (amountUsdBn.gt(factQuoteAmountBn)) {
      const diffQuote = subtractBignumbers(
        [amountUsdBn, ORDER_CALC_DECIMALS],
        [factQuoteAmountBn, ORDER_CALC_DECIMALS],
      );
      quoteAmountBn = addBignumbers(
        [quoteAmountBn, ORDER_CALC_DECIMALS],
        [diffQuote, ORDER_CALC_DECIMALS],
      );
    }
  }

  return { baseAmountBn, quoteAmountBn };
}

function applyOrdersToEntries(params: {
  entries: ProcessedOrderbookEntry[];
  side: OrderbookSide;
  ordersByPrice: Map<string, CexOrder[]>;
}): ProcessedOrderbookEntry[] {
  const { entries, side, ordersByPrice } = params;

  if (entries.length === 0) return entries;

  const nextEntries = [...entries];
  let accumulatedBaseBn = bn(0, ORDER_CALC_DECIMALS);
  let accumulatedQuoteBn = bn(0, ORDER_CALC_DECIMALS);
  let accumulatedCount = 0;

  const startIndex = side === 'ask' ? entries.length - 1 : 0;
  const endIndex = side === 'ask' ? -1 : entries.length;
  const step = side === 'ask' ? -1 : 1;

  for (let i = startIndex; i !== endIndex; i += step) {
    const entry = entries[i];
    if (!entry || entry.isPlaceholder) {
      nextEntries[i] = entry;
      continue;
    }

    const entryOrders = ordersByPrice.get(entry.price) ?? [];
    const { baseAmountBn, quoteAmountBn } = sumOrderAmounts(entryOrders);

    accumulatedBaseBn = addBignumbers(
      [accumulatedBaseBn, ORDER_CALC_DECIMALS],
      [baseAmountBn, ORDER_CALC_DECIMALS],
    );
    accumulatedQuoteBn = addBignumbers(
      [accumulatedQuoteBn, ORDER_CALC_DECIMALS],
      [quoteAmountBn, ORDER_CALC_DECIMALS],
    );
    accumulatedCount += entryOrders.length;

    nextEntries[i] = {
      ...entry,
      hasOrders: entryOrders.length > 0,
      ordersCurrentAmount: Math.max(0, Number(humanizeBn(accumulatedBaseBn, ORDER_CALC_DECIMALS))),
      ordersCurrentAmountQuote: Math.max(
        0,
        Number(humanizeBn(accumulatedQuoteBn, ORDER_CALC_DECIMALS)),
      ),
      ordersCurrentCount: accumulatedCount,
    };
  }

  return nextEntries;
}

/**
 * NO SAFE FALLBACK EXISTS FOR TICK SIZE
 *
 * Tick size depends on the specific BASE/QUOTE pair, not just the quote currency.
 * Even for USD-stablecoin pairs, values vary drastically:
 *
 * Examples from Binance:
 * - BTCUSDT:  tickSize = 0.01        (high-price coin)
 * - DOGEUSDT: tickSize = 0.00001     (sub-dollar coin)
 * - SHIBUSDT: tickSize = 0.00000001  (micro-priced memecoin)
 *
 * - BTCTRY:   tickSize = 1.0         (fiat, integer steps)
 * - BNBBTC:   tickSize = 0.000001    (crypto-quoted)
 * - BNBETH:   tickSize = 0.0001      (different from BTC!)
 *
 * Using wrong tick size causes silent data corruption:
 * - SHIBUSDT with 0.01 fallback → all prices collapse to "0.00"
 * - DOGEUSDT with 0.01 fallback → hundreds of levels collapse to 2 buckets
 * - User sees "valid" orderbook but aggregation is completely broken
 *
 * SOLUTION: Always show error when APIs fail. Better than showing wrong data.
 */

/**
 * Main CEX Orderbook component with real-time updates and virtualization
 * Optimized for high-frequency data changes
 */
export const CexOrderbook = memo(function CexOrderbook({
  pair,
  className,
  externalControl,
  onSelectPriceAmount,
  orders,
}: CexOrderbookProps) {
  const [hoveredIndexAsk, setHoveredIndexAsk] = useState<number | null>(null);
  const [hoveredIndexBid, setHoveredIndexBid] = useState<number | null>(null);
  const [hoveredSide, setHoveredSide] = useState<OrderbookSide | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // Track scrolling state per-list to handle race conditions
  // isScrolling should only be false when BOTH lists are not scrolling
  const isAskScrollingRef = useRef(false);
  const isBidScrollingRef = useRef(false);

  const arrowRef = useRef<SVGSVGElement | null>(null);

  // Use stable middleware configuration - flip will try all placements automatically
  // The order preference: left first, then right, then vertical based on available space
  const { refs, floatingStyles, update, context } = useFloating({
    placement: 'left',
    middleware: [
      offset(15),
      flip({
        // Comprehensive fallback list - flip will find the best fit automatically
        fallbackPlacements: ['right', 'top', 'bottom'],
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>();
  const [indicatorRef, { height: indicatorHeight }] = useMeasure<HTMLDivElement>();

  // Access orderbook data from store
  const { asks, bids, lastPrice, isLoading, error } = useOrderbookData(pair.id);

  // Access and manage settings from store
  const { tickSize, baseTickSize, viewMode, setTickSize, setViewMode } = useOrderbookSettings(
    pair.id,
  );
  const isExternallyManaged = typeof externalControl?.tickSize !== 'undefined';
  const tickSizeError =
    !isExternallyManaged && tickSize === null && !isLoading
      ? 'Failed to determine tick size for the selected pair.'
      : null;

  const showDepthNotice = useMemo(() => {
    const limitedExchanges = [
      ECexName.kucoin,
      ECexName.gate,
      ECexName.whitebit,
      ECexName.bingx,
      ECexName.bitmart,
      ECexName.hotcoin,
      ECexName.coinstore,
      ECexName.indodax,
    ];
    if (!limitedExchanges.includes(pair.cex)) return false;
    if (!Number.isFinite(tickSize ?? NaN) || !Number.isFinite(baseTickSize ?? NaN)) return false;
    return Number(tickSize) > Number(baseTickSize);
  }, [pair.cex, tickSize, baseTickSize]);

  const noticeText = useMemo(() => {
    switch (pair.cex) {
      case ECexName.kucoin:
        return 'KuCoin orderbook is limited to 100 levels on public APIs. With larger tick size the book can look sparse.';
      case ECexName.gate:
        return 'Gate.io orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.whitebit:
        return 'WhiteBIT orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.bingx:
        return 'BingX orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.bitmart:
        return 'Bitmart orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.hotcoin:
        return 'Hotcoin orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.coinstore:
        return 'Coinstore orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      case ECexName.indodax:
        return 'Indodax orderbook is limited to 100 levels. With larger tick size the book can look sparse.';
      default:
        return '';
    }
  }, [pair.cex]);

  // Reverse asks for display (Binance-style: lowest ask near spread/bottom)
  // Preserve placeholder rows at the top so we never leave empty space at the bottom.
  // Real asks are reversed for display, placeholders stay prepended.
  const displayAsks = useMemo(() => {
    const placeholders = asks.filter(entry => entry.isPlaceholder);
    const realAsks = asks.filter(entry => !entry.isPlaceholder).reverse();
    return [...placeholders, ...realAsks];
  }, [asks]);

  const ordersByPrice = useMemo(() => {
    if (!orders) return null;

    const effectiveTickSize = tickSize ?? externalControl?.tickSize ?? null;
    const askMap = new Map<string, CexOrder[]>();
    const bidMap = new Map<string, CexOrder[]>();

    for (const order of orders) {
      if (order.pair_id !== pair.id) continue;

      const side: OrderbookSide = order.side === ETradeDirection.sell ? 'ask' : 'bid';
      const roundedPrice = roundPriceToTick(order.price, effectiveTickSize, side);
      const targetMap = side === 'ask' ? askMap : bidMap;
      const existing = targetMap.get(roundedPrice);

      if (existing) {
        existing.push(order);
      } else {
        targetMap.set(roundedPrice, [order]);
      }
    }

    return { askMap, bidMap };
  }, [orders, pair.id, tickSize, externalControl?.tickSize]);

  const displayAsksWithOrders = useMemo(() => {
    if (!ordersByPrice) return displayAsks;
    return applyOrdersToEntries({
      entries: displayAsks,
      side: 'ask',
      ordersByPrice: ordersByPrice.askMap,
    });
  }, [displayAsks, ordersByPrice]);

  const bidsWithOrders = useMemo(() => {
    if (!ordersByPrice) return bids;
    return applyOrdersToEntries({
      entries: bids,
      side: 'bid',
      ordersByPrice: ordersByPrice.bidMap,
    });
  }, [bids, ordersByPrice]);

  const visibleAsks = useMemo(
    () =>
      showDepthNotice
        ? displayAsksWithOrders.filter(entry => !entry.isPlaceholder)
        : displayAsksWithOrders,
    [displayAsksWithOrders, showDepthNotice],
  );

  const visibleBids = useMemo(
    () => (showDepthNotice ? bidsWithOrders.filter(entry => !entry.isPlaceholder) : bidsWithOrders),
    [bidsWithOrders, showDepthNotice],
  );

  const hasAsks = visibleAsks.length > 0;
  const hasBids = visibleBids.length > 0;

  type LayoutMode = 'both' | 'ask-only' | 'bid-only' | 'empty';

  const layoutMode: LayoutMode = useMemo(() => {
    if (viewMode === 'ask') {
      return hasAsks ? 'ask-only' : 'empty';
    }
    if (viewMode === 'bid') {
      return hasBids ? 'bid-only' : 'empty';
    }

    // viewMode === 'all'
    if (hasAsks && hasBids) {
      return 'both';
    }
    if (hasAsks) {
      return 'ask-only';
    }
    if (hasBids) {
      return 'bid-only';
    }

    return 'empty';
  }, [viewMode, hasAsks, hasBids]);

  const indicatorVisible = Boolean(lastPrice);

  const { askHeight, bidHeight } = useMemo(() => {
    const effectiveIndicatorHeight = indicatorVisible ? indicatorHeight : 0;
    const availableHeight = Math.max(contentHeight - effectiveIndicatorHeight, 0);

    switch (layoutMode) {
      case 'ask-only':
        return { askHeight: availableHeight, bidHeight: 0 };
      case 'bid-only':
        return { askHeight: 0, bidHeight: availableHeight };
      case 'both':
        return { askHeight: availableHeight / 2, bidHeight: availableHeight / 2 };
      default:
        return { askHeight: 0, bidHeight: 0 };
    }
  }, [contentHeight, indicatorHeight, indicatorVisible, layoutMode]);

  const handleRowClick = useCallback(
    (price: string, amount: string) => {
      onSelectPriceAmount?.(price, amount);
    },
    [onSelectPriceAmount],
  );

  // Store the current reference element to detect if it's still valid
  const currentReferenceRef = useRef<HTMLElement | null>(null);

  const handleAskHover = useCallback(
    (index: number | null, element?: HTMLElement | null) => {
      setHoveredIndexAsk(index);
      if (index !== null && element) {
        setHoveredSide('ask');
        currentReferenceRef.current = element;
        refs.setReference(element);
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          update();
        });
      } else if (index === null) {
        currentReferenceRef.current = null;
        // Use functional update to avoid stale closure - only clear if currently 'ask'
        setHoveredSide(current => (current === 'ask' ? null : current));
      }
    },
    [refs, update],
  );

  const handleBidHover = useCallback(
    (index: number | null, element?: HTMLElement | null) => {
      setHoveredIndexBid(index);
      if (index !== null && element) {
        setHoveredSide('bid');
        currentReferenceRef.current = element;
        refs.setReference(element);
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          update();
        });
      } else if (index === null) {
        currentReferenceRef.current = null;
        // Use functional update to avoid stale closure - only clear if currently 'bid'
        setHoveredSide(current => (current === 'bid' ? null : current));
      }
    },
    [refs, update],
  );

  // Separate scroll handlers for ask and bid lists to prevent race conditions
  // Only set isScrolling to false when BOTH lists have stopped scrolling
  const handleAskScrollingChange = useCallback(
    (scrolling: boolean) => {
      isAskScrollingRef.current = scrolling;
      const anyScrolling = scrolling || isBidScrollingRef.current;
      setIsScrolling(anyScrolling);
      // When all scrolling stops, if we still have a hovered element, update tooltip position
      if (!anyScrolling && currentReferenceRef.current) {
        requestAnimationFrame(() => {
          update();
        });
      }
    },
    [update],
  );

  const handleBidScrollingChange = useCallback(
    (scrolling: boolean) => {
      isBidScrollingRef.current = scrolling;
      const anyScrolling = scrolling || isAskScrollingRef.current;
      setIsScrolling(anyScrolling);
      // When all scrolling stops, if we still have a hovered element, update tooltip position
      if (!anyScrolling && currentReferenceRef.current) {
        requestAnimationFrame(() => {
          update();
        });
      }
    },
    [update],
  );

  // Show error if tick size couldn't be determined
  if (!isExternallyManaged && tickSizeError) {
    return (
      <div className={cn('flex items-center justify-center h-full p-4', className)}>
        <div className="text-center">
          <p className="text-sm text-red-500 mb-2">{tickSizeError}</p>
          <p className="text-xs text-zinc-500">
            Please check your network connection or try again later
          </p>
        </div>
      </div>
    );
  }

  // Show error if orderbook data failed to load
  if (error) {
    return (
      <div className={cn('flex items-center justify-center h-full p-4', className)}>
        <p className="text-sm text-red-500">Failed to load orderbook data</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full w-full bg-zinc-950', className)}>
      {/* Toolbar */}
      <OrderbookToolbar
        tickSize={tickSize}
        baseTickSize={baseTickSize}
        lastPrice={lastPrice}
        viewMode={viewMode}
        exchange={pair.cex}
        onTickSizeChange={setTickSize}
        onViewModeChange={setViewMode}
      />

      {showDepthNotice && (
        <div className="px-2 py-1 text-[10px] text-orange-400 bg-orange-400/10 border-b border-orange-400/20">
          {noticeText}
        </div>
      )}

      {/* Table headers */}
      <div className="flex items-center w-full px-3 py-2 border-b border-zinc-800">
        <div className="w-[35%] text-left text-zinc-400 text-[11px]">
          Price ({pair.token_quote.symbol})
        </div>
        <div className="w-[33%] text-right text-zinc-400 text-[11px]">
          Amount ({pair.token_base.symbol})
        </div>
        <div className="w-[32%] text-right text-zinc-400 text-[11px]">
          Total ({pair.token_quote.symbol})
        </div>
      </div>

      {/* Orderbook content */}
      <div ref={contentRef} className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-zinc-500">Loading orderbook...</p>
          </div>
        ) : (
          <>
            {/* Asks list */}
            {askHeight > 0 && (
              <VirtualizedOrderbookList
                type="ask"
                data={visibleAsks}
                height={askHeight}
                lastPrice={lastPrice}
                onRowClick={handleRowClick}
                onRowHover={handleAskHover}
                hoveredIndex={hoveredIndexAsk}
                onScrollingChange={handleAskScrollingChange}
              />
            )}

            {/* Indicator between asks/bids */}
            {indicatorVisible && (
              <div ref={indicatorRef}>
                <LastPriceIndicator price={lastPrice} quoteSymbol={pair.token_quote.symbol} />
              </div>
            )}

            {/* Bids list */}
            {bidHeight > 0 && (
              <VirtualizedOrderbookList
                type="bid"
                data={visibleBids}
                height={bidHeight}
                lastPrice={lastPrice}
                onRowClick={handleRowClick}
                onRowHover={handleBidHover}
                hoveredIndex={hoveredIndexBid}
                onScrollingChange={handleBidScrollingChange}
              />
            )}
          </>
        )}
      </div>

      {/* Hover tooltip */}
      <OrderbookHoverTooltip
        hoveredSide={hoveredSide}
        hoveredIndex={hoveredSide === 'ask' ? hoveredIndexAsk : hoveredIndexBid}
        asks={visibleAsks}
        bids={visibleBids}
        isScrolling={isScrolling}
        floatingStyles={floatingStyles}
        context={context}
        setFloating={refs.setFloating}
        arrowRef={arrowRef}
        lastPrice={lastPrice}
        baseSymbol={pair.token_base.symbol}
        quoteSymbol={pair.token_quote.symbol}
      />
    </div>
  );
});
